import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowLeftRight,
  BarChart3,
  Box,
  Briefcase,
  Building,
  Building2,
  ChevronDown,
  FileText,
  Key,
  LayoutDashboard,
  LogOut,
  Map,
  MapPin,
  Menu as MenuIcon,
  Moon,
  Package,
  PackageCheck,
  PackageSearch,
  Settings,
  ShieldCheck,
  Sun,
  Truck,
  User,
  UserCircle,
  UserCog,
  Users,
  Warehouse,
} from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { useTema } from '../context/ThemeContext'
import { alerta } from '../utils/alertas'
import { PERMISOS } from '../utils/permisos'
import type { Tema } from '../types'

const GRUPOS = [
  {
    titulo: 'Operacion',
    items: [
      { ruta: '/', texto: 'Panel', icono: LayoutDashboard, permiso: PERMISOS.panel.ver },
      { ruta: '/inventario', texto: 'Inventario', icono: Package, permiso: PERMISOS.inventario.ver },
      { ruta: '/movimientos', texto: 'Movimientos', icono: ArrowLeftRight, permiso: PERMISOS.movimientos.ver },
      { ruta: '/pedidos', texto: 'Pedidos', icono: Truck, permiso: PERMISOS.pedidos.ver },
      { ruta: '/recepciones', texto: 'Recepciones', icono: Warehouse, permiso: PERMISOS.recepciones.ver },
      { ruta: '/surtido', texto: 'Surtido', icono: PackageSearch, permiso: PERMISOS.surtido.ver },
      { ruta: '/packing', texto: 'Packing', icono: PackageCheck, permiso: PERMISOS.packing.ver },
      { ruta: '/embarques', texto: 'Embarques', icono: Truck, permiso: PERMISOS.embarques.ver },
      { ruta: '/rutas', texto: 'Rutas', icono: Map, permiso: PERMISOS.rutas.ver },
      { ruta: '/transporte', texto: 'Transporte', icono: MapPin, permiso: PERMISOS.transporte.ver },
      { ruta: '/analiticas', texto: 'Analiticas', icono: BarChart3, permiso: PERMISOS.analiticas.ver },
    ]
  },
  {
    titulo: 'Catalogos',
    items: [
      { ruta: '/almacenes', texto: 'Almacenes', icono: Building2, permiso: PERMISOS.almacenes.ver },
      { ruta: '/ubicaciones', texto: 'Ubicaciones', icono: MapPin, permiso: PERMISOS.ubicaciones.ver },
      { ruta: '/productos', texto: 'Productos', icono: Package, permiso: PERMISOS.productos.ver },
      { ruta: '/clientes', texto: 'Clientes', icono: UserCircle, permiso: PERMISOS.clientes.ver },
      { ruta: '/proveedores', texto: 'Proveedores', icono: Briefcase, permiso: PERMISOS.proveedores.ver },
      { ruta: '/transportistas', texto: 'Transportistas', icono: Truck, permiso: PERMISOS.transportistas.ver },
      { ruta: '/vehiculos', texto: 'Vehiculos', icono: Truck, permiso: PERMISOS.vehiculos.ver },
    ]
  },
  {
    titulo: 'Configuracion',
    items: [
      { ruta: '/empresas', texto: 'Empresas', icono: Building, permiso: PERMISOS.empresas.ver },
      { ruta: '/usuarios', texto: 'Usuarios', icono: Users, permiso: PERMISOS.usuarios.ver },
      { ruta: '/perfiles', texto: 'Perfiles', icono: User, permiso: PERMISOS.perfiles.ver },
      { ruta: '/roles', texto: 'Roles y permisos', icono: ShieldCheck, permiso: PERMISOS.roles.ver },
      { ruta: '/licencias', texto: 'Licencias', icono: Key, permiso: PERMISOS.licencias.ver },
      { ruta: '/configuracion', texto: 'Configuracion', icono: Settings, permiso: PERMISOS.configuracion.ver },
    ]
  },
  {
    titulo: 'Reportes',
    items: [
      { ruta: '/reportes', texto: 'Reportes', icono: FileText, permiso: PERMISOS.reportes.ver },
    ]
  }
]

export function Layout() {
  const { usuario, cerrarSesion, inicializando } = useAuth()
  const { tema, modoOscuro, cambiarTema, alternarOscuro, TEMAS } = useTema()
  const navegar = useNavigate()
  const ubicacion = useLocation()
  const [menuAbierto, setMenuAbierto] = useState(false)
  const [headerAbierto, setHeaderAbierto] = useState(false)

  useEffect(() => {
    if (!inicializando && !usuario) {
      navegar('/login', { replace: true })
    }
  }, [inicializando, usuario, navegar])

  const grupos = GRUPOS

  const [gruposAbiertos, setGruposAbiertos] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(GRUPOS.map((g) => [g.titulo, g.items.some((i) => i.ruta === ubicacion.pathname)]))
  )

  const alternarGrupo = (titulo: string) =>
    setGruposAbiertos((prev) => ({ ...prev, [titulo]: !prev[titulo] }))

  const salir = async () => {
    if (!(await alerta.confirmar('Cerrar sesion', 'Se cerrara la sesion actual.', 'Cerrar sesion'))) return
    await cerrarSesion()
    navegar('/login')
  }

  const temaActivo = TEMAS.find((t) => t.valor === tema) ?? TEMAS[0]

  return (
    <div className="app grid h-full" style={{ gridTemplateColumns: '1fr' }}>
      <aside
        className={`barra-lateral backdrop-blur-sm ${menuAbierto ? 'abierta' : ''}`}
        style={{ color: '#fff', border: '1px solid rgba(128, 128, 128, 0.6)', backgroundColor: 'rgba(31, 41, 55, 0.65)', borderTopRightRadius: '2rem', borderBottomRightRadius: '2rem' }}
      >
        <div className="marca">
          <Box size={22} />
          <span>{usuario?.empresa ?? 'Logistica'}</span>
        </div>
        {grupos.map((grupo) => {
          const abierto = !!gruposAbiertos[grupo.titulo]
          return (
            <div key={grupo.titulo}>
              <button
                type="button"
                className="nav-grupo flex w-full items-center justify-between cursor-pointer bg-transparent border-0 "
                onClick={() => alternarGrupo(grupo.titulo)}
                aria-expanded={abierto}
              >
                <span className="text-white font-bold" style={{ textShadow: '0 2px 10px rgba(0, 0, 0, 01.85)' }}>{grupo.titulo}</span>
                <ChevronDown size={20} className={`transition-transform duration-300 ${abierto ? 'rotate-180' : ''} text-white font-bold`} />
              </button>
              {abierto &&
                grupo.items.map(({ ruta, texto, icono: Icono }) => (
                  <NavLink
                    key={ruta}
                    to={ruta}
                    end={ruta === '/'}
                    className={({ isActive }) => `nav-item ${isActive ? 'activo' : ''}`}
                    onClick={() => setMenuAbierto(false)}
                  >
                    <Icono size={20} className="text-white bg-accent/80 border border-white rounded-full p-0.3" />
                    <span className="text-white">{texto}</span>
                  </NavLink>
                ))}
            </div>
          )
        })}
      </aside>

      {menuAbierto && (
        <div
          className="fixed inset-0 z-[45] bg-black/5"
          onClick={() => setMenuAbierto(false)}
          aria-hidden
        />
      )}

      {headerAbierto && (
        <div
          className="fixed inset-0 z-[35] bg-black/5"
          onClick={() => setHeaderAbierto(false)}
          aria-hidden
        />
      )}

      <button
        className="btn btn-icono fixed bottom-4 left-4 z-50 shadow-md"
        onClick={() => setMenuAbierto((v) => !v)}
        title="Menu"
      >
        <MenuIcon size={22} />
      </button>

      <button
        className="btn btn-icono fixed top-4 right-4 z-50 shadow-md"
        onClick={() => setHeaderAbierto((v) => !v)}
        title="Configuracion"
      >
        <Settings size={22} />
      </button>

      {!['/', '/inventario'].includes(ubicacion.pathname) && (
        <button
          className="btn btn-icono fixed bottom-4 right-4 z-50 shadow-md rounded-full"
          onClick={() => navegar('/inventario')}
          title="Inventario"
        >
          <Package size={22} />
        </button>
      )}

      <div className="contenido flex flex-col min-w-0 h-full">
        <header
          className={`encabezado backdrop-blur-sm pr-17 flex justify-end ${headerAbierto ? 'abierto' : ''}`}
          style={{ backgroundColor: 'rgba(128, 128, 128, 0.38)' }}
        >
          <div className="fila flex-wrap">
            <div className="fila w-48">
              <span
                className="w-6 h-6 rounded-full border border-borde"
                style={{ background: temaActivo.muestra }}
              />
              <select
                value={tema}
                onChange={(e) => cambiarTema(e.target.value as Tema)}
                className="flex-1 text-white border border-white/30 rounded-full bg-gray-800/40 "
                title="Tema de color"
              >
                {TEMAS.map((t) => (
                  <option key={t.valor} value={t.valor}>{t.nombre}</option>
                ))}
              </select>
            </div>

            <button className="btn btn-plano btn-icono border-gray-400 dark:border-white/50" onClick={alternarOscuro} title="Modo oscuro">
              {modoOscuro ? <Sun size={18} className=" dark:!text-white" /> : <Moon size={18} className="text-white !text-gray-500 dark:text-white" />}
            </button>

            <div className="fila" style={{ gap: 6 }}>
              <UserCog size={18} />
              <div className="leading-tight">
                <div className="text-[0.85rem] font-bold ">{usuario?.nombreCompleto}</div>
                <div className="text-texto-suave text-[0.72rem]">{usuario?.rol}</div>
              </div>
            </div>

            <button className="btn btn-peligro btn-icono border-red-500" onClick={salir} title="Cerrar sesion">
              <LogOut size={18} className="text-red-400" />
            </button>
          </div>
        </header>

        <main className="vista anima-entrada overflow-auto p-1">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
